/**
 * The Apps tab (design "Flow Project Redesign", Apps board): one page per service. On the left Start all / Stop all and
 * the project's services (state, the one selected); on the right the selected one: its name, how long it has run and
 * what it uses, Open · Stop · Restart · Logs, its problem with the build's own message, and below a row of tabs
 * (Overview, Config, API, Quality, Traces, Data, Logs, Code, Git) of which Overview summarises how it runs and what to do
 * next. Each other tab shows its part for this service in this page (components/apps/ServiceTabs), with "Open …" to the
 * project's full tab already on this service; a tab the plan hides is left out, with its link. A Docker Compose app
 * (its own compose file, or the runner's `runMode`) leads the list with a "Compose" badge. A flagged service shows
 * "Read-only" / "Context only" in the list and on its page, where the project's owner turns the flags on or off
 * (components/repoFlags). "Open preview" shows the service in the project's Preview tab (the `collab:openPreview`
 * event Canvas listens for); "Make default app" saves it as the project's default app (utils/defaultApp: what Open app,
 * the Preview gadget and a new share link open), which then shows a "Default app" tag.
 * "Found in this service" (FoundRunnables) lists the Dockerfiles and compose files found in the service's folder, to pin
 * or ignore; a pinned one is an item of the list under its service ("Compose" or
 * "Dockerfile" badge, a compose file's services under it), run as a session of its own, with its page (RunnableDetail).
 * A Docker Compose app found outside `services/` (OutsideCompose) is a notice on top, with Move to services/<name>.
 * How it runs, on the Overview, picks the service's builder (BuilderPicker: its code stack, Makefile target, Dockerfile or
 * compose file); the list names the one in use under each service, and a running service offers "Restart with this
 * builder" once it changed. A build that found no stack ("No supported stack detected") says so in the problem box
 * without the runner's "add .flow/service.json" advice, and offers "Set how it starts" (StartSetup: How it runs in a
 * dialog, then "Save and start") and "or edit .flow/service.json" (opens it in Code).
 * A pinned file sits under its service in the list as a child row (indented, a line on its left, named by its file
 * alone: "docker-compose.yaml"); its page is titled "app · docker-compose.yaml", may be the default app too (saved as
 * that file, utils/defaultApp). Code, before Logs, opens the service's folder or the pinned file in Code. A session that crashed shows its last lines
 * (the runner's `crashOutput`, else the tail of its stderr) in the Problem box.
 * A service run as a Docker Compose app has its "Preview entry point" on its Overview (saved in its .flow/service.json;
 * "Restart to apply" stops it and starts it again, so the runner reads the file anew). `focus: { service }` (a short
 * name, a `services/<name>` path or a pinned file's runner name: ⌘K's "Open <service> app") selects that service, with
 * `section` its sub-tab. The selection is kept in the URL (`?tab=apps&service=<name>&section=<tab>`, replaced on each
 * pick; the first service and Overview left out), so Back, a reload and a copied link return to it.
 * An editor's "⋯" next to the actions has "Delete service…" (DeleteServiceDialog: what goes, what still uses it, the
 * name typed to confirm); once deleted the list shows the first service again and a message offers Undo and History.
 * A native device app ("iOS app (Swift)", "Android app (Kotlin)", utils/deviceApps.js: its builders, its Build for
 * devices entry or its runner entry say so) never runs in the sandbox: an "iOS app" / "Android app" chip instead of its
 * state, no Open preview, no Start or Restart (Code stands alone), left out of Start all, a note in place of a problem
 * (the runner's "This is a native app" is information), and Build for devices.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, message as toast } from 'antd';
import { Eye } from '@phosphor-icons/react';
import useProjectServices from '../../hooks/useProjectServices';
import {
  formatBytes, formatCores, formatUptime, getServiceStatus, isRunning, serviceState, sessionIdFor, startService, stopService,
} from '../../services/SandboxService';
import RunAllActions from '../sandbox/RunAllActions';
import { foldPinned } from '../sandbox/projectRun';
import ServiceActions from '../sandbox/ServiceActions';
import ServiceState from '../sandbox/ServiceState';
import { shortServiceName } from '../../utils/gitRepoSource';
import { isComposeService } from '../../utils/serviceStackModel';
import useServiceFolders, { notifyServicesChanged } from '../../hooks/useServiceFolders';
import { isNoStackError, noStackSummary, serviceBuilderSummary } from '../../utils/builderModel';
import useRunnables from '../../hooks/useRunnables';
import { parseRunnableName, pinnedItems, runnableServiceName } from '../../utils/runnablesModel';
import NoDockerNote from './NoDockerNote';
import FoundRunnables from './FoundRunnables';
import RunnableDetail from './RunnableDetail';
import OutsideCompose from './OutsideCompose';
import ServiceOverview from './ServiceOverview';
import { DeviceBuildButton } from '../devicebuilds/DeviceBuildEntry';
import useDeviceBuilds from '../../hooks/useDeviceBuilds';
import { mobileAppOf } from '../../services/DeviceBuildsService';
import { deviceAppOf, isNativeAppError } from '../../utils/deviceApps';
import StartSetup from './StartSetup';
import { AddServiceMenu, useAddService } from '../home/AddService';
import ServiceTabPanel from './ServiceTabs/ServiceTabPanel';
import useRepoFlags from '../../hooks/useRepoFlags';
import { serviceRepo } from '../../utils/repoFlags';
import RepoFlagBadges from '../repoFlags/RepoFlagBadges';
import RepoFlagControls from '../repoFlags/RepoFlagControls';
import useDeployFlags from '../../hooks/useDeployFlags';
import { NotDeployedBadge } from './DeploySwitch';
import useDefaultApp from '../../hooks/useDefaultApp';
import { openPreviewFor } from '../presence/Presence';
import { searchWithAppsSelection } from '../../utils/workspaceModel';
import DeleteServiceDialog, { showDeletedMessage } from './DeleteServiceDialog';
import './AppsPanel.css';
import './AppsList.css';

const PROBLEMS = { crashed: 'The process stopped by itself.', failed: 'The build failed.' };

/**
 * The tabs of a service's page after Overview, each shown in this page for the service. `full`: its "Open …" link
 * opens that project tab on this service (`onOpenView`); Logs opens the Logs view (`onOpenLogs`); Code links itself.
 */
export const SERVICE_TABS = [
  { tab: 'config', label: 'Config', title: 'Configuration', text: 'Variables, secrets and scheduled jobs', full: true },
  { tab: 'api', label: 'API', title: 'API', text: 'Endpoints; send a test request', full: true },
  { tab: 'quality', label: 'Quality', title: 'Quality', text: 'Tests, lint, security; what blocks Publish', full: true },
  { tab: 'traces', label: 'Traces', title: 'Traces', text: 'Requests through this service', full: true },
  { tab: 'data', label: 'Data', title: 'Data', text: 'Records in the datastores', full: true },
  { tab: 'logs', label: 'Logs', title: 'Logs', text: 'What it printed last' },
  { tab: 'code', label: 'Code', title: 'Code', text: 'Its files' },
  { tab: 'git', label: 'Git', title: 'Git', text: 'Branch, changes and remotes; pull, push', full: true },
];
// Kept for the cards' test ids and links.
export const SERVICE_CARDS = SERVICE_TABS;

/** The first line of a build's error, so the banner stays a sentence. */
const firstLine = (text) => String(text || '').split('\n').map((line) => line.trim()).find(Boolean) || '';

/** Lines that say nothing (blank) left out. */
const textLines = (list) => (Array.isArray(list) ? list : []).map((line) => String(typeof line === 'string' ? line : line?.message ?? line?.line ?? '').trim()).filter(Boolean);

/**
 * The last lines a crashed session printed: the runner's `crashOutput` (up to 5, stderr first; on its status' session
 * or its row in the project's services), else the last lines of its stderr (`recentLogs.stderr`). [] when none.
 */
export function crashLines(info, row = null, max = 5) {
  const own = textLines(info?.crashOutput);
  if (own.length) { return own.slice(-max); }
  const listed = textLines(row?.crashOutput);
  if (listed.length) { return listed.slice(-max); }
  return textLines(info?.recentLogs?.stderr).slice(-max);
}

/**
 * The Problem box's sentence for a service in `state` (`crashed`, `failed`): a crash with output says what it printed
 * last ("The stack failed to start: <line>" for a compose file, "It stopped by itself: <line>" otherwise), a failed
 * build its error's first line; else "… Read the log to see why.". `lines`: the crash's lines when more than one (shown
 * under it). null when there is no problem.
 */
export function problemOf({ state, info = null, row = null, compose = false }) {
  if (!PROBLEMS[state]) { return null; }
  if (state === 'crashed') {
    const lines = crashLines(info, row);
    if (lines.length) {
      return { text: `${compose ? 'The stack failed to start' : 'It stopped by itself'}: ${lines[lines.length - 1]}`, lines: lines.length > 1 ? lines : [] };
    }
  }
  // The runner's own "Build failed: " prefix is the box's first words already.
  const message = firstLine(info?.buildError).replace(/^Build failed:\s*/i, '');
  return { text: `${PROBLEMS[state]}${message ? ` ${message}` : ' Read the log to see why.'}`, lines: [] };
}

/** A problem with the runner's "No supported stack detected" in it, without its .flow/service.json advice. */
const tidyProblem = (text) => {
  const at = text.indexOf('No supported stack detected');
  return at < 0 ? text : `${text.slice(0, at)}${noStackSummary(text)}`;
};

/** The service a focus names (a short name, a `services/<name>` path or a pinned file's runner name), as the list names it. */
const pickedOf = (focus) => {
  const wanted = typeof focus?.service === 'string' ? focus.service.trim() : '';
  if (!wanted) { return null; }
  return wanted.startsWith('services/') ? wanted : `services/${wanted}`;
};
const SECTION_IDS = ['overview', ...SERVICE_TABS.map((item) => item.tab)];
/** The sub-tab a focus names (`section`), or null. */
const sectionOf = (focus) => (SECTION_IDS.includes(focus?.section) ? focus.section : null);

export default function AppsPanel({
  projectId, onOpenView = null, onOpenLogs = null, shows = () => true, readOnly = false, focus = null,
}) {
  const { services: running, canRun, refresh, loaded } = useProjectServices(projectId, { enabled: Boolean(projectId), intervalMs: 5000 });
  // Apps › Deploy this service (utils/deployFlags.js): the list and the page say which services are not deployed.
  const deploy = useDeployFlags(projectId);
  const repoFlags = useRepoFlags(projectId);
  const { defaultApp, setDefaultApp } = useDefaultApp(projectId);
  const [savingDefault, setSavingDefault] = useState(false);
  // The project's services are its `services/<name>` folders (the store Home's Apps and the header read too:
  // hooks/useServiceFolders); the runner only knows the ones that ran. Read again when the runner's count changes.
  const { folders, composed } = useServiceFolders(projectId, { refreshKey: running.length });
  // Bumped after a service was added or moved: the runnables are read again (the folders through notifyServicesChanged).
  const [added, setAdded] = useState(0);
  const isCompose = (item) => (item.runnable ? item.runnable.kind === 'compose'
    : composed.includes(item.serviceName) || isComposeService({ runMode: item.runMode }));
  // The Dockerfiles and compose files found in the services, and the ones pinned (run as sessions of their own).
  const runnables = useRunnables(projectId, { refreshKey: added });
  // The project's mobile services (GET /device-builds › apps): a native one is a device app (utils/deviceApps.js).
  const { data: deviceBuilds } = useDeviceBuilds(projectId);
  const deviceOf = (item) => (!item || item.runnable ? null : deviceAppOf({
    builders: runnables.scan?.builders?.[item.serviceName] || null,
    app: mobileAppOf(deviceBuilds, shortServiceName(item.serviceName)),
    service: item,
  }));
  // Bumped when How it runs rewrote a service's .flow/service.json: its Overview reads it again.
  const [specVersion, setSpecVersion] = useState(0);
  // The service whose builder just changed while it ran ("Restart with this builder"), and a restart asked from there.
  const [restartOffer, setRestartOffer] = useState(null);
  const [restartRequest, setRestartRequest] = useState(0);
  // "Set how it starts" open (a build with no stack), and the start asked by its "Save and start".
  const [setupOpen, setSetupOpen] = useState(false);
  const [startRequest, setStartRequest] = useState(0);
  const own = running.filter((item) => !parseRunnableName(item.serviceName));
  const pinned = pinnedItems(runnables.scan, runnables.choices).map((item) => ({
    ...(running.find((entry) => entry.serviceName === item.serviceName) || { serviceName: item.serviceName, projectId, buildStatus: 'idle', containerStatus: 'stopped' }),
    runnable: item,
  }));
  // A pinned file's session that still runs after it was unpinned: listed, so it can be stopped.
  const unpinned = running.filter((entry) => parseRunnableName(entry.serviceName) && isRunning(entry) && !pinned.some((item) => item.serviceName === entry.serviceName))
    .map((entry) => ({ ...entry, runnable: { ...parseRunnableName(entry.serviceName), serviceName: entry.serviceName, file: null, services: [] } }));
  const extra = [...pinned, ...unpinned];
  const base = [
    ...(folders || []).map((name) => own.find((item) => item.serviceName === name) || { serviceName: name, projectId, buildStatus: 'idle', containerStatus: 'stopped' }),
    ...own.filter((item) => !(folders || []).includes(item.serviceName)),
  ].filter((item, index, all) => all.findIndex((other) => other.serviceName === item.serviceName) === index)
    // A compose app first: it is usually the whole project.
    .sort((a, b) => Number(isCompose(b)) - Number(isCompose(a)));
  // Each pinned file right under its service.
  const services = base.flatMap((item) => [item, ...extra.filter((entry) => entry.runnable.service === item.serviceName)]);
  services.push(...extra.filter((entry) => !services.includes(entry)));
  // The selected service and sub-tab start from the view's focus (a link, a reload, Back: `?tab=apps&service=…&section=…`).
  const [picked, setPicked] = useState(() => pickedOf(focus));
  const [tab, setTab] = useState(() => sectionOf(focus) || 'overview');
  // "+ Add service": the Home's own flow (a new empty service, a .zip, a folder, a git repository); the new one is selected.
  const adder = useAddService(projectId, {
    taken: (folders || []).map((name) => shortServiceName(name)),
    onDone: (result) => {
      setAdded((n) => n + 1);
      notifyServicesChanged(projectId);
      refresh();
      const first = result?.services?.[0];
      if (first) { setPicked(first.startsWith('services/') ? first : `services/${first}`); setTab('overview'); }
    },
  });
  const addControls = readOnly ? null : (
    <>
      <AddServiceMenu onAdd={adder.add}><button className="apps-add" data-testid="apps-add" type="button">+ Add service</button></AddServiceMenu>
      {adder.state?.text && <p className="apps-muted" role="status">{adder.state.text}</p>}
      {adder.dialogs}
    </>
  );
  const [info, setInfo] = useState(null);
  // Why the last Start, Stop or Restart of the selected service failed (ServiceActions' `onError`): in the problem box.
  const [actionError, setActionError] = useState(null);
  // "Delete service…" open (an editor, on a service's own page).
  const [deleteOpen, setDeleteOpen] = useState(false);
  // A refused Start's "Provide the missing files" (focus.provideMissing): the pinned file's form opens.
  const [provideRequest, setProvideRequest] = useState(0);
  // A service asked from outside (⌘K): selected on its Overview, each time it is asked (a new `focus`).
  useEffect(() => {
    const wanted = typeof focus?.service === 'string' ? focus.service.trim() : '';
    if (!wanted) { return; }
    setPicked(pickedOf(focus));
    setTab(sectionOf(focus) || 'overview');
    setActionError(null);
    setSetupOpen(false);
    if (focus?.provideMissing) { setProvideRequest((n) => n + 1); }
  }, [focus]);
  const names = services.map((service) => service.serviceName);
  const selectedName = names.includes(picked) ? picked : names[0] || null;
  const tabs = SERVICE_TABS.filter((item) => shows(item.tab));
  const current = tabs.some((item) => item.tab === tab) ? tab : 'overview';
  // The selection in the URL (`?tab=apps&service=<name>&section=<tab>`, replace: no history entry per click), so Back,
  // a reload and a copied link come back to it. The first service and Overview are left out; a service the list no
  // longer has falls back to the first one, its param cleaned. Only once the list (with the pinned files) is read.
  const listReady = loaded && folders !== null && runnables.loaded !== false && names.length > 0;
  const urlService = selectedName && selectedName !== names[0] ? shortServiceName(selectedName) : null;
  const urlSection = current === 'overview' ? null : current;
  useEffect(() => {
    if (!listReady || typeof window === 'undefined') { return; }
    const next = searchWithAppsSelection(window.location.search, { service: urlService, section: urlSection });
    if (next !== window.location.search) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${next}${window.location.hash}`);
    }
  }, [listReady, urlService, urlSection]);
  const service = services.find((item) => item.serviceName === selectedName) || null;
  const state = serviceState(service);
  const status = service?.buildStatus;

  // What the runner says about the selected service: its port and, when it failed, the build's own message.
  useEffect(() => {
    if (!selectedName) { setInfo(null); return undefined; }
    let live = true;
    getServiceStatus(service?.sessionId || sessionIdFor(projectId, selectedName)).then((found) => { if (live) { setInfo(found || null); } }).catch(() => { if (live) { setInfo(null); } });
    return () => { live = false; };
    // Read again when it crashes or stops too: the crash's last lines come with the status.
  }, [projectId, selectedName, status, service?.sessionId, service?.crashed, service?.containerStatus]);

  if (!loaded || folders === null) { return <div className="apps-panel" data-testid="apps-panel"><p className="apps-empty">Loading the services…</p></div>; }
  // A compose app outside services/ (Flow does not run it there): a notice, with Move to services/<name>.
  const outside = runnables.scan?.outside?.length ? (
    <OutsideCompose
      found={runnables.scan.outside}
      onMoved={(moved) => { setAdded((n) => n + 1); notifyServicesChanged(projectId); refresh(); setPicked(moved); setTab('overview'); }}
      projectId={projectId}
      readOnly={readOnly}
      taken={folders.map((name) => shortServiceName(name))}
    />
  ) : null;
  if (services.length === 0) {
    return <div className="apps-panel" data-testid="apps-panel"><div className="apps-empty">{outside}<p>This project has no service yet.</p>{addControls}</div></div>;
  }

  const open = (target, focus = true) => onOpenView?.({ tab: target, focus: focus ? { service: shortServiceName(selectedName) } : null });
  // A tab's "Open …": the project tab on this service, or the Logs view; Code makes its own link.
  const openFull = (target) => {
    if (target === 'logs') { return onOpenLogs ? () => onOpenLogs(selectedName) : null; }
    return SERVICE_TABS.find((item) => item.tab === target)?.full && onOpenView ? () => open(target) : null;
  };
  // Start all starts the project's services and the pinned files started before (the runner knows their session) or
  // that are the default app; Stop all stops what runs. A service its running pinned file carries (no stack of its own,
  // or a failed build, next to a compose file that runs) is left out (projectRun foldPinned).
  // A native device app is never started (built for phones): left out unless something of it still runs.
  const items = foldPinned(services.filter((item) => !item.runnable || isRunning(item) || Boolean(item.sessionId) || defaultApp === shortServiceName(item.serviceName))
    .filter((item) => !deviceOf(item) || isRunning(item))
    .map((item) => ({ serviceName: item.serviceName, service: item, runnable: item.runnable?.file ? item.runnable : null })));
  const runnable = service?.runnable || null;
  const found = runnable ? [] : runnables.scan?.services?.[selectedName]?.runnables || [];
  const titleOf = (item) => (item.runnable ? `${shortServiceName(item.runnable.service)} · ${item.runnable.file || item.runnable.kind}` : shortServiceName(item.serviceName));
  // A pinned file under its service in the list: its file alone (the service is the row above).
  const isChild = (item) => Boolean(item.runnable) && names.includes(item.runnable.service);
  // A service whose own process does not run while a pinned file of it does: it runs through that file (neutral, not
  // its own failed build).
  const carrierOf = (item) => (item.runnable || isRunning(item) ? null
    : services.find((entry) => entry.runnable?.service === item.serviceName && isRunning(entry) && entry.buildStatus !== 'building') || null);
  const rowTitle = (item) => (isChild(item) ? item.runnable.file || item.runnable.kind : titleOf(item));
  const noDocker = Boolean(runnable) && runnables.docker === false && !isRunning(service);
  const device = deviceOf(service);
  const stats = [
    isRunning(service) ? `running ${formatUptime(service?.startedAt)}` : device ? 'built for phones, not run in the sandbox' : 'not running',
    service?.cpuPercent === undefined ? null : `${formatCores(service.cpuPercent)} CPU`,
    service?.memoryBytes === undefined ? null : formatBytes(service.memoryBytes),
  ].filter(Boolean).join(' · ');
  // A crash says what it printed last (problemOf); a failed build its error's first line.
  // A device app has no run problem: the runner's "This is a native app" is information (the note below).
  const foundProblem = actionError || device ? null : problemOf({ state, info, row: service, compose: runnable ? runnable.kind === 'compose' : isCompose(service || {}) });
  const problemLines = foundProblem?.lines || [];
  // A compose app whose services none answers HTTP (the runner's entry point `error`): said in the box too.
  const entryProblem = (runnable ? runnable.kind === 'compose' : isCompose(service || {})) && info?.composeEntry?.error ? info.composeEntry.error : null;
  const rawProblem = (device && isNativeAppError(actionError) ? null : actionError) || foundProblem?.text || entryProblem || null;
  // Nothing in the folder the runner starts by itself: the person says how it starts here (not only in the file).
  const noStack = !runnable && Boolean(rawProblem) && isNoStackError(rawProblem);
  const problem = rawProblem && noStack ? tidyProblem(rawProblem) : rawProblem;
  const canStart = canRun && !readOnly && !noDocker && !device;
  // The run controls (Start, Stop, Restart, Logs): not for a device app, unless something of it still runs.
  const runControls = canRun && !readOnly && !noDocker && (!device || isRunning(service));
  // Code, before Logs: the service's folder, or a pinned file itself (its compose file or Dockerfile).
  const codePath = runnable ? (runnable.file ? runnable.path || `${runnable.service}/${runnable.file}` : runnable.service) : selectedName;
  const codeButton = (
    <Button data-testid="apps-open-code" onClick={() => window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path: codePath } }))} size="middle" title={`Open ${codePath} in Code`}>
      Code
    </Button>
  );
  const openSpecFile = () => window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path: `${selectedName}/.flow/service.json` } }));
  const shortName = shortServiceName(selectedName);
  const isDefault = defaultApp === shortName;
  // A pinned file may be the default app too (saved as that file, utils/defaultApp); one still running after it was
  // unpinned (no file) may not.
  const canBeDefault = !runnable || Boolean(runnable.file);
  // "Restart to apply" a pinned compose file's new entry point: the runner keeps the runnable a session started with,
  // so it is stopped and rendered again with the saved one (runnable.entry).
  const applyEntry = async() => {
    const sessionId = service?.sessionId || sessionIdFor(projectId, selectedName);
    const saved = runnables.choices.find((item) => item.path === runnable.path)?.entry || null;
    await stopService(sessionId);
    await startService(projectId, runnable.service, { runnable: { ...runnable, entry: saved } });
    refresh();
  };
  // The same for a Docker Compose app's own entry point (its .flow/service.json): a start re-reads the service's files,
  // a plain restart of a running process would not.
  const applyServiceEntry = async() => {
    await stopService(service?.sessionId || sessionIdFor(projectId, selectedName));
    await startService(projectId, selectedName);
    refresh();
  };
  // Who may run the services may delete one (an editor or the owner); a pinned file is unpinned, not deleted.
  const canDelete = canRun && !readOnly && !runnable && String(selectedName || '').startsWith('services/');
  const servicesChanged = () => { setAdded((n) => n + 1); notifyServicesChanged(projectId); refresh(); };
  const onDeleted = (done) => {
    setDeleteOpen(false);
    setPicked(null);
    setTab('overview');
    setActionError(null);
    servicesChanged();
    // Undo puts it back (files, pins, default app, secrets, jobs) and shows it again.
    showDeletedMessage(projectId, done, { onRestored: () => { servicesChanged(); setPicked(`services/${done.service}`); } });
  };
  const makeDefault = async() => {
    setSavingDefault(true);
    try {
      await setDefaultApp(shortName, runnable ? { runnable } : undefined);
      toast.success(`${titleOf(service)} is the default app`);
    } catch (error) {
      toast.error(error.message || 'The default app could not be saved');
    } finally {
      setSavingDefault(false);
    }
  };

  return (
    <div className="apps-panel" data-testid="apps-panel">
      <aside aria-label="Services" className="apps-list">
        <div className="apps-cap">Services</div>
        {canRun && !readOnly && <div className="apps-runall" data-testid="apps-run-all"><RunAllActions onChanged={refresh} onOpenLogs={onOpenLogs || undefined} projectId={projectId} services={items} size="middle" /></div>}
        {services.map((item) => {
          const on = item.serviceName === selectedName;
          return (
            <button
              aria-current={on ? 'true' : undefined}
              className={`apps-item${on ? ' is-on' : ''}${isChild(item) ? ' is-child' : ''}`}
              data-child-of={isChild(item) ? item.runnable.service : undefined}
              data-testid={`apps-item-${item.serviceName}`}
              key={item.serviceName}
              onClick={() => { if (item.serviceName !== selectedName) { setTab('overview'); setActionError(null); setSetupOpen(false); } setPicked(item.serviceName); }}
              type="button"
            >
              <span className="apps-item-text">
                <span className="apps-item-name">
                  <span className="apps-item-label" title={titleOf(item)}>{rowTitle(item)}</span>
                  {isCompose(item) && <span className="apps-badge" data-testid={`apps-compose-badge-${item.serviceName}`}>Compose</span>}
                  {item.runnable?.kind === 'dockerfile' && <span className="apps-badge" data-testid={`apps-dockerfile-badge-${item.serviceName}`}>Dockerfile</span>}
                  {!item.runnable && <RepoFlagBadges flags={repoFlags.serviceFlags(item.serviceName)} testId={`apps-flags-${item.serviceName}`} />}
                  {!item.runnable && !deploy.isDeployed(item.serviceName) && <NotDeployedBadge testId={`apps-not-deployed-${item.serviceName}`} />}
                </span>
                <span className="apps-item-sub">
                  {carrierOf(item)
                    ? <span className="apps-item-carried" data-testid={`apps-carried-${item.serviceName}`}>Runs through {carrierOf(item).runnable.file || carrierOf(item).runnable.kind}</span>
                    : <ServiceState device={deviceOf(item)} service={item} />}
                </span>
                {!item.runnable && serviceBuilderSummary(runnables.scan?.builders?.[item.serviceName]) && (
                  <span className="apps-item-builder" data-testid={`apps-item-builder-${item.serviceName}`}>{serviceBuilderSummary(runnables.scan.builders[item.serviceName])}</span>
                )}
                {item.runnable?.services?.length > 0 && (
                  <span className="apps-subitems" data-testid={`apps-subitems-${item.serviceName}`}>
                    {item.runnable.services.map((entry) => <span className="apps-subitem" key={entry.name}>{entry.name}</span>)}
                  </span>
                )}
              </span>
            </button>
          );
        })}
        {addControls}
      </aside>
      <main className="apps-detail" data-testid="apps-detail">
        {outside}
        <section className="apps-card">
          <div className="apps-head">
            <div className="apps-head-main">
              <div className="apps-title">
                <h2>{titleOf(service)}</h2>
                <ServiceState device={device} service={service} />
                {isDefault && <span className="apps-tag is-accent apps-default-tag" data-testid="apps-default-app" title={`Open app, the Preview gadget and new share links open this ${runnable ? 'file' : 'service'}`}>Default app</span>}
                <RepoFlagBadges flags={repoFlags.serviceFlags(selectedName)} testId="apps-detail-flags" />
                {!runnable && selectedName && !deploy.isDeployed(selectedName) && <NotDeployedBadge testId="apps-detail-not-deployed" />}
              </div>
              <div className="apps-stats">{stats}</div>
              <div className="apps-chips">
                {info?.previewPort && <span className="apps-tag">Port {info.previewPort}</span>}
                {service?.runMode && <span className="apps-tag is-accent" data-testid="apps-runmode">Runs as: {service.runMode}</span>}
                {/* History › Activity filtered on this service (the same as `?tab=activity&service=<name>`). */}
                {onOpenView && <button className="apps-info-link apps-small" data-testid="apps-open-activity" onClick={() => open('activity')} type="button">See its activity</button>}
                {!readOnly && canBeDefault && !isDefault && services.length > 1 && (
                  <button className="apps-info-link apps-small" data-testid="apps-make-default" disabled={savingDefault} onClick={makeDefault} title={`Open app, the Preview gadget and new share links will open this ${runnable ? 'file' : 'service'}`} type="button">
                    {savingDefault ? 'Saving…' : 'Make default app'}
                  </button>
                )}
              </div>
              {!runnable && serviceRepo(selectedName) && <RepoFlagControls projectId={projectId} repo={serviceRepo(selectedName)} />}
            </div>
            <div className="apps-actions">
              {shows('preview') && !device && (
                <Button data-testid="apps-open-preview" icon={<Eye aria-hidden size={14} />} onClick={() => openPreviewFor(projectId, { service: selectedName, route: '/' })} size="middle" title="Show this service in the project's Preview tab">
                  Open preview
                </Button>
              )}
              <DeviceBuildButton projectId={projectId} readOnly={readOnly} service={shortName} />
              {canRun && !readOnly && noDocker && (
                <span className="apps-no-docker" data-testid="apps-runnable-no-docker"><NoDockerNote reason={runnables.dockerOff} /></span>
              )}
              {/* Without the run controls (a viewer, or no Docker for a pinned file), Code stands on its own. */}
              {!runControls && codeButton}
              {runControls && (
                <ServiceActions beforeLogs={codeButton} key={selectedName} onChanged={refresh} restartRequest={restartRequest} startRequest={startRequest} onError={setActionError} runnable={runnable} onOpenLogs={onOpenLogs ? () => onOpenLogs(selectedName) : undefined} projectId={projectId} service={service} serviceName={selectedName} showRemove={false} size="middle" />
              )}
              {canDelete && (
                <Dropdown
                  menu={{ items: [{ key: 'delete', label: 'Delete service…', danger: true }], onClick: ({ key }) => { if (key === 'delete') { setDeleteOpen(true); } } }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button aria-label={`More for ${shortName}`} data-testid="apps-more" size="middle" title="More">⋯</Button>
                </Dropdown>
              )}
            </div>
          </div>
        </section>
        {canDelete && deleteOpen && (
          <DeleteServiceDialog onClose={() => setDeleteOpen(false)} onDeleted={onDeleted} open projectId={projectId} service={selectedName} />
        )}
        {device && (
          <section className="apps-device-note" data-testid="apps-device-note" role="status">
            <strong>{device.label}</strong>
            <span>
              It runs on phones, not in the sandbox preview: build it with Build for devices
              {device.platforms.length ? ` (${device.platforms.map((platform) => (platform === 'ios' ? 'iPhone' : 'Android')).join(', ')})` : ''}.
            </span>
          </section>
        )}
        {problem && (
          <section className="apps-problem" data-testid="apps-problem" role="alert">
            <strong>Problem</strong>
            <span className="apps-problem-text" data-testid="apps-problem-text">{problem}</span>
            {problemLines.length > 0 && <pre className="apps-problem-lines" data-testid="apps-problem-lines">{problemLines.join('\n')}</pre>}
            {noStack && !readOnly && (
              <span className="apps-problem-actions">
                <Button data-testid="apps-set-start" onClick={() => setSetupOpen(true)} type="primary">Set how it starts</Button>
                <button className="apps-problem-file" data-testid="apps-edit-service-json" onClick={openSpecFile} type="button">or edit .flow/service.json</button>
              </span>
            )}
            {onOpenLogs && <button className="apps-link" onClick={() => onOpenLogs(selectedName)} type="button">Read the log</button>}
          </section>
        )}
        {noStack && !readOnly && (
          <StartSetup
            canStart={canStart}
            docker={runnables.docker}
            info={runnables.scan?.builders?.[selectedName] || null}
            onClose={() => setSetupOpen(false)}
            onSaved={() => {
              setSpecVersion((n) => n + 1);
              runnables.reload();
              setActionError(null);
              if (canStart) { setStartRequest((n) => n + 1); }
            }}
            open={setupOpen}
            projectId={projectId}
            serviceName={selectedName}
          />
        )}
        {found.length > 0 && (
          <FoundRunnables
            choices={runnables.choices}
            docker={runnables.docker}
            howItRuns={!runnables.scan?.builders}
            onChoose={runnables.choose}
            onOpen={(item) => { setPicked(runnableServiceName(selectedName, item)); setActionError(null); }}
            onSpecChanged={() => setSpecVersion((n) => n + 1)}
            projectId={projectId}
            readOnly={readOnly}
            runnables={found}
            serviceName={selectedName}
          />
        )}
        {runnable ? (
          <RunnableDetail
            entry={info?.composeEntry || null}
            failure={state === 'failed' || state === 'crashed' ? problem : null}
            item={runnable}
            onProvided={() => { runnables.reload(); setActionError(null); }}
            projectId={projectId}
            provideRequest={provideRequest}
            onApplyEntry={runnable.file && canRun && !readOnly ? applyEntry : null}
            onSaveEntry={runnable.file && runnable.path && !readOnly ? (entry) => runnables.setEntry(runnable.path, entry) : null}
            readOnly={readOnly || !canRun}
            runner={service}
            sessionId={service?.sessionId || sessionIdFor(projectId, selectedName)}
          />
        ) : (
          <section className="apps-card apps-tabs-card">
            <div className="apps-tabs" role="tablist">
              {[{ tab: 'overview', label: 'Overview' }, ...tabs].map((item) => (
                <button
                  aria-selected={item.tab === current}
                  className={`apps-tab${item.tab === current ? ' is-on' : ''}`}
                  data-testid={`apps-tab-${item.tab}`}
                  key={item.tab}
                  onClick={() => setTab(item.tab)}
                  role="tab"
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>
            {current === 'overview' ? (
              <ServiceOverview
                key={`${selectedName}:${specVersion}`}
                builders={runnables.scan?.builders?.[selectedName] || null}
                docker={runnables.docker}
                offerRestart={restartOffer === selectedName && isRunning(service) && canRun}
                onBuilderSaved={() => {
                  setSpecVersion((n) => n + 1);
                  runnables.reload();
                  setRestartOffer(isRunning(service) ? selectedName : null);
                }}
                onRestart={() => { setRestartOffer(null); setRestartRequest((n) => n + 1); }}
                onApplyEntry={canStart ? applyServiceEntry : null}
                readOnly={readOnly}
                info={info}
                defaultApp={defaultApp}
                device={device}
                names={names.map(shortServiceName)}
                onOpen={onOpenView ? open : null}
                onOpenLogs={onOpenLogs ? () => onOpenLogs(selectedName) : null}
                onTab={setTab}
                projectId={projectId}
                runner={service}
                service={shortServiceName(selectedName)}
                shows={shows}
              />
            ) : (
              <ServiceTabPanel
                onOpenFull={openFull(current)}
                projectId={projectId}
                readOnly={readOnly}
                service={shortServiceName(selectedName)}
                serviceName={selectedName}
                sessionId={service?.sessionId || null}
                tab={current}
              />
            )}
          </section>
        )}
      </main>
    </div>
  );
}

AppsPanel.propTypes = {
  // `{ service }`: the service to select (⌘K's "Open <service> app").
  focus: PropTypes.object,
  onOpenLogs: PropTypes.func,
  onOpenView: PropTypes.func,
  projectId: PropTypes.string,
  readOnly: PropTypes.bool,
  // (tabId) => whether the plan shows that tab.
  shows: PropTypes.func,
};
